/**
 * Componente de Botão (Primário e Secundário)
 * Segue as especificações do design system da Vivo
 */

export interface ButtonConfig {
  text: string;
  onClick?: (e: MouseEvent) => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  id?: string;
  className?: string;
  loading?: boolean;
}

export class ButtonComponent {
  /**
   * Cria um botão primário seguindo o design system
   */
  static createPrimaryButton(config: ButtonConfig): HTMLButtonElement {
    const button = document.createElement('button');
    
    // Configurações básicas
    button.type = config.type || 'button';
    button.disabled = config.disabled || false;
    
    if (config.id) {
      button.id = config.id;
    }
    
    // Classes CSS
    const classes = ['btn-primary-component'];
    if (config.fullWidth) {
      classes.push('full-width');
    }
    if (config.className) {
      classes.push(config.className);
    }
    if (config.loading) {
      classes.push('btn-loading');
    }
    button.className = classes.join(' ');
    
    // Conteúdo do botão (texto ou loading)
    this.updateButtonContent(button, config.text, config.loading || false);
    
    // Event listeners
    if (config.onClick) {
      button.addEventListener('click', config.onClick);
    }
    
    return button;
  }

  /**
   * Cria um botão secundário seguindo o design system
   */
  static createSecondaryButton(config: ButtonConfig): HTMLButtonElement {
    const button = document.createElement('button');
    
    // Configurações básicas
    button.type = config.type || 'button';
    button.disabled = config.disabled || false;
    
    if (config.id) {
      button.id = config.id;
    }
    
    // Classes CSS
    const classes = ['btn-secondary-component'];
    if (config.fullWidth) {
      classes.push('full-width');
    }
    if (config.className) {
      classes.push(config.className);
    }
    if (config.loading) {
      classes.push('btn-loading');
    }
    button.className = classes.join(' ');
    
    // Conteúdo do botão (texto ou loading)
    this.updateButtonContent(button, config.text, config.loading || false);
    
    // Event listeners
    if (config.onClick) {
      button.addEventListener('click', config.onClick);
    }
    
    return button;
  }

  /**
   * Atualiza o conteúdo do botão (texto ou spinner de loading)
   */
  static updateButtonContent(button: HTMLButtonElement, text: string, loading: boolean): void {
    if (loading) {
      // Limpar conteúdo anterior e adicionar spinner
      button.textContent = '';
      button.innerHTML = '<span class="btn-spinner"></span>';
      button.classList.add('btn-loading');
    } else {
      // Limpar HTML e restaurar texto
      button.innerHTML = '';
      button.textContent = text;
      button.classList.remove('btn-loading');
    }
  }

  /**
   * Define o estado de loading de um botão
   */
  static setLoading(button: HTMLButtonElement, loading: boolean, text?: string): void {
    if (loading) {
      // Armazenar texto original se ainda não foi armazenado
      // Pegar o texto do innerText ou textContent, removendo espaços em branco
      if (!button.dataset.originalText) {
        const currentText = button.innerText?.trim() || button.textContent?.trim() || text || '';
        button.dataset.originalText = currentText;
      }
      button.disabled = true;
      this.updateButtonContent(button, text || button.dataset.originalText || '', true);
    } else {
      button.disabled = false;
      // Restaurar texto original ou usar o texto fornecido
      const originalText = button.dataset.originalText || text || '';
      this.updateButtonContent(button, originalText, false);
      // Limpar dados temporários
      delete button.dataset.originalText;
    }
  }
}

